import styled from '@emotion/styled';
import { Button, Stack, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { ArrayTranslation } from '@hotjar/translations';
import { InvoiceNotifications } from '~/subscription-management/invoices/components/InvoiceNotifications';
import { SUBSCRIPTIONS } from '../../../../constants/routes';
import { navigateTo } from '../../../../external-dependencies/webapp-utils';
import processSuccessGraphic from '../../../../resources/images/processed-success.svg';
import { Image, TextWithLineBreak } from '../ProcessOrderContainer.styles';
import {
  PriceReviewNextRecurring,
  PriceReviewTodayCharge,
} from '../../edit-order/review-order/PriceReviewHelpers';
import type { Context } from '~/subscription-management/checkout/state-machine/checkout.machine.types';

interface Props {
  subscriptionEndDate?: string;
  context: Pick<
    Context,
    'accountDiscounts' | 'accountId' | 'userId' | 'customer' | 'billingPeriod' | 'costs'
  >;
}

const S = {
  Container: styled.div`
    padding: ${tokens.spacer.x3};
    display: flex;
    flex-direction: column;
    align-items: center;
    height: 100%;
    width: 100vw;
  `,
  WidthContainer: styled.div`
    text-align: left;
    width: 800px;
    flex-grow: 1;
    display: flex;
    align-items: center;
  `,
  ContentContainer: styled(Stack)`
    align-items: flex-start;
  `,
  LeftSideStack: styled(Stack)`
    min-width: 478px;
  `,
  Image: styled(Image)`
    align-self: flex-start;
  `,
  PriceReviewTodayCharge: styled(PriceReviewTodayCharge)`
    background-color: ${tokens.ALIAS_COLORS.bg_info};
  `,
  PriceReviewNextRecurring: styled(PriceReviewNextRecurring)`
    background-color: ${tokens.ALIAS_COLORS.bg_info};
  `,
  Button: styled(Button)`
    align-self: flex-end;
  `,
};

const FORM_ID = 'invoice-notifications-form';

export function ProcessOrderSuccess({ subscriptionEndDate, context }: Props): JSX.Element {
  const { t } = useTranslation('checkout', { keyPrefix: 'process-order.success' });
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const {
    accountId,
    userId,
    customer,
    accountDiscounts,
    billingPeriod,
    costs: { renewWith, payNow },
  } = context;

  return (
    <S.Container>
      <S.WidthContainer>
        <Stack spacing="x8" direction="column">
          <S.Image src={processSuccessGraphic} alt="" />

          <S.ContentContainer spacing="x7pt5" direction="row">
            <S.LeftSideStack spacing="x3" direction="column">
              <TextWithLineBreak as="h1" textType="header4">
                {t('title')}
              </TextWithLineBreak>
              <div>
                <ArrayTranslation
                  t={t}
                  i18nKey="body"
                  options={{ emailAddress: customer.billingAddress?.emailAddress }}
                  mapperFunction={(paragraph: string) => (
                    <TextWithLineBreak key={paragraph} as="p" textType="body2">
                      {paragraph}
                    </TextWithLineBreak>
                  )}
                />
              </div>

              <S.PriceReviewTodayCharge
                currency={customer.currency}
                payNowAmountWithTax={payNow?.amountWithTax ?? 0}
                taxAmount={renewWith?.taxAmount}
                loading={false}
              />

              <S.PriceReviewNextRecurring
                currency={customer.currency}
                billingPeriod={billingPeriod}
                renewWithAmountWithTax={renewWith?.amountWithTax ?? 0}
                taxAmount={renewWith?.taxAmount}
                accountDiscounts={accountDiscounts}
                nextChargeDate={subscriptionEndDate}
                loading={false}
              />
            </S.LeftSideStack>
            <InvoiceNotifications
              accountId={accountId}
              userId={userId}
              shouldSkipViewingState
              onUpdate={() => navigateTo(SUBSCRIPTIONS)}
              externalButton={{ formId: FORM_ID, setLoading, setDisabled, setTouched }}
              narrow
            />
          </S.ContentContainer>
        </Stack>
      </S.WidthContainer>

      {touched ? (
        <S.Button type="submit" form={FORM_ID} isLoading={loading} isDisabled={disabled}>
          {t('action.back-to-hotjar-and-save-notifications')}
        </S.Button>
      ) : (
        <S.Button onClick={() => navigateTo(SUBSCRIPTIONS)}>{t('action.back-to-hotjar')}</S.Button>
      )}
    </S.Container>
  );
}
